import React, { useState } from 'react';
import { Box, TextField, Button, CircularProgress, Typography, Paper, Divider, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Search, UserCheck } from 'lucide-react'; //Upload
import { Prospect } from '../../types/savvydata.ts';
import api from '../../services/savvydata/api.ts';

const FormContainer = styled(Paper)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const FormField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiInputBase-root': {
    color: '#fff',
    backgroundColor: 'rgba(26, 26, 46, 0.4)',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#8A2BE2',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  background: 'linear-gradient(45deg, #8A2BE2 30%, #00CC99 90%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(45deg, #9370DB 30%, #00FFD1 90%)',
    boxShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
  },
}));

const SearchField = styled(FormField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    paddingRight: 0,
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 0,
  },
}));

interface ProspectEnrichmentFormProps {
  onProspectEnriched: (prospect: Prospect) => void;
}

const ProspectEnrichmentForm: React.FC<ProspectEnrichmentFormProps> = ({ onProspectEnriched }) => {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    industry: '',
    website: '',
    linkedin_url: '',
    location: '',
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchingSalesforce, setSearchingSalesforce] = useState(false);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create prospect
      const prospect = await api.prospects.createProspect(formData);
      
      // Enrich prospect
      const enrichedProspect = await api.prospects.enrichProspect(prospect.id);
      
      onProspectEnriched(enrichedProspect);
    } catch (error) {
      console.error('Error enriching prospect:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSalesforceSearch = async () => {
    setSearchingSalesforce(true);
    
    try {
      // Mock search functionality to return results based on searchTerm
      if (searchTerm.toLowerCase().includes('nick') || searchTerm.toLowerCase().includes('baguley')) {
        // Simulate delay for realistic API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setFormData({
          name: 'Nick Baguley',
          company_name: 'Baguley LLC',
          industry: 'Technology',
          website: 'https://baguleyllc.com',
          linkedin_url: 'https://linkedin.com/in/nickbaguley',
          location: 'Salt Lake City, UT',
        });
      } else if (searchTerm.trim() !== '') {
        // If search term doesn't match "Nick Baguley", mock a "no results" scenario
        await new Promise(resolve => setTimeout(resolve, 1000));
        alert('No contacts found matching your search.');
      } else {
        // If empty search
        alert('Please enter a search term.');
      }
    } catch (error) {
      console.error('Error searching Salesforce:', error);
    } finally {
      setSearchingSalesforce(false);
    }
  };
  
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSalesforceSearch();
    }
  };
  
  return (
    <FormContainer>
      <Typography variant="h6" sx={{ mb: 2, color: '#00ffff' }}>
        Prospect Enrichment
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
          Search for a prospect in Salesforce or enter details manually.
          </Typography>
        
        <SearchField
          placeholder="Search Salesforce"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          fullWidth
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  variant="contained"
                  onClick={handleSalesforceSearch}
                  disabled={searchingSalesforce}
                  sx={{ 
                    borderRadius: '0 4px 4px 0',
                    height: '56px',
                    background: 'rgba(138, 43, 226, 0.8)',
                    '&:hover': {
                      background: 'rgba(138, 43, 226, 1)',
                    }
                  }}
                >
                  {searchingSalesforce ? <CircularProgress size={24} color="inherit" /> : <Search />}
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      <Divider sx={{ my: 2, borderColor: 'rgba(138, 43, 226, 0.2)' }} />
      
      <form onSubmit={handleSubmit}>
        <FormField
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          fullWidth
          variant="outlined"
        />
        
        <FormField
          label="Company"
          name="company_name"
          value={formData.company_name}
          onChange={handleInputChange}
          fullWidth
          variant="outlined"
        />
        
        <FormField
          label="Industry"
          name="industry"
          value={formData.industry}
          onChange={handleInputChange}
          fullWidth
          variant="outlined"
        />
        
        <FormField
          label="Website"
          name="website"
          value={formData.website}
          onChange={handleInputChange}
          fullWidth
          variant="outlined"
        />
        
        <FormField
          label="LinkedIn URL"
          name="linkedin_url"
          value={formData.linkedin_url}
          onChange={handleInputChange}
          fullWidth
          variant="outlined"
        />
        
        <FormField
          label="Location"
          name="location"
          value={formData.location}
          onChange={handleInputChange}
          fullWidth
          variant="outlined"
        />
        
        <ActionButton
          type="submit"
          fullWidth
          disabled={loading || !formData.name || !formData.company_name}
          startIcon={loading ? <CircularProgress size={24} /> : <UserCheck />}
        >
          {loading ? 'Processing...' : 'Enrich Prospect'}
        </ActionButton>
      </form>
    </FormContainer>
  );
};

export default ProspectEnrichmentForm; 